/**
 * 可催運盤：日時區間掃描＋排除＋生剋／本宮辨識
 *
 * 排除（硬性，與關係模式無關）：
 *   1) 八門伏吟／八門反吟／天干伏吟／天干反吟（引擎 menFuyin／ganFuyin）
 *   2) 日干本宮（日干落宮）有 空亡／刑／墓／迫
 *   3) 任一「生我」宮有 空亡／刑／墓／迫
 *   4) 「生我」宮或「日干本宮」有天干組合 庚+丙／丙+庚
 *      （天盤干＋地盤干，含寄顯 jiXianTian／jiXianDi）
 *
 * 關係模式（UI 互斥：生我／我剋／不限／本宮）：
 *   生我／我剋 — 層＋細項落在洛書生我／我剋宮
 *   本宮 — 層＋細項須落在日干本宮（例：本宮＋生門＝日干落宮門＝生；
 *          本宮本身已硬排除刑迫空墓／庚丙）
 *   不限 — 不依生剋／本宮細項過濾（仍過硬排除）
 *
 * 生我／我剋：洛書宮位五行 vs 日干落宮五行（非天盤干五行）
 *   生我 = 該宮洛書五行 生 日干落宮五行
 *   我剋 = 日干落宮五行 剋 該宮洛書五行
 *   **不含中宮（洛書5）**：使用者不排中宮；中五天盤星因 EE/EN 不飛布
 *   固定為地盤禽→顯示芮，若納入生剋會令「我剋芮」等幾乎永遠命中中五。
 *   星層只認八方宮之天盤星（真飛布的芮在非5宮）；中五芮不參與匹配。
 *
 * 層（天干／門／星／神）：
 *   - 無細項 → 該宮有該層內容即通過（存在過濾）
 *   - 有細項（如僅生門）→ 該宮該層值須命中選集；可與生我／我剋／本宮併用
 *
 * 跨層生剋（可選，與日干落宮生我／我剋／本宮並用 AND）：
 *   Item A + 生|剋 + Item B（各為天干／門／星／神之一具體符號）
 *   命中＝八宮內找含 A／含 B 的宮，存在一對 (Pa,Pb) 使 Pa 洛書五行 生/剋 Pb 洛書五行
 *   （只用洛書宮位五行，與日干落宮生我／我剋同表；不用乙木／芮土等固有五行）
 *   例：乙剋芮＝乙宮洛書剋飛布芮宮洛書；合生乙＝合宮洛書生乙宮洛書
 *   「須同宮」預設關：Pa≠Pb 可；開＝Pa===Pb（同宮五行同→生/剋通常0）
 */

import { computeQimen, type QimenResult, type PalaceContent } from '../engine/qimen';
import {
  type CuiyunLayer,
  type Wuxing,
  isShengWo,
  isWoKe,
  stemWuxing,
  palaceWuxing,
  layerMatches,
  layerItemId,
  layerItemLabel,
  LAYER_LABEL,
  normalizeLayerItem,
  palacesElementRelate,
} from './wuxing';

export type RelationFilter = '生我' | '我剋' | '本宮' | null;

/** 各層已選細項（空＝該層不限細項，僅存在過濾） */
export type LayerItemFilters = Partial<Record<CuiyunLayer, string[]>>;

/** 跨層生剋關係：A 所在宮洛書 生/剋 B 所在宮洛書 */
export type CrossLayerRel = '生' | '剋';

/** 跨層一端：層＋具體符號 id（引擎正規化值，如伤／芮／阴） */
export interface CrossLayerSide {
  layer: CuiyunLayer;
  id: string;
}

/**
 * 跨層生剋過濾（可選）。
 * 三者皆備（a、relation、b）才啟用；否則忽略。
 * samePalace 預設 false＝異宮可（比對兩宮洛書五行）；true＝須同宮。
 */
export interface CrossLayerFilter {
  a: CrossLayerSide;
  relation: CrossLayerRel;
  b: CrossLayerSide;
  /** 須同宮；預設 false */
  samePalace?: boolean;
}

/** 一對命中：A 落 Pa、B 落 Pb，且 Pa 洛書 生/剋 Pb 洛書 */
export interface CrossLayerPair {
  aPalace: number;
  bPalace: number;
  aWx: Wuxing;
  bWx: Wuxing;
}

/** 跨層生剋命中詳情 */
export interface CrossLayerHit {
  aPalaces: number[];
  bPalaces: number[];
  /** 滿足洛書生剋的 (Pa,Pb) 對 */
  pairs: CrossLayerPair[];
  /** 命中對涉及的宮 */
  highlightPalaces: number[];
  /** 如「乙@震3(木)剋芮@坤2(土)」 */
  label: string;
  samePalace: boolean;
}

export interface CuiyunSearchInput {
  from: { year: number; month: number; day: number; hour: number; minute: number };
  to: { year: number; month: number; day: number; hour: number; minute: number };
  /** 啟用層：天干／門／星／神；預設四層全開 */
  layers?: CuiyunLayer[];
  /**
   * 各層細項（如 men:['生']＝僅生門）。
   * 未列或空陣列＝該層不限細項（存在過濾）。
   * id 用引擎值（伤／开／惊／冲／辅／阴）；繁體亦接受並正規化。
   */
  layerItems?: LayerItemFilters;
  /** 生我／我剋；null＝不過濾關係 */
  relation?: RelationFilter;
  /**
   * 跨層生剋（洛書宮位五行）。完整指定才過濾；可與 relation 並用（AND）。
   */
  crossLayer?: CrossLayerFilter | null;
  /**
   * 載入時是否一併高亮日干本宮（與關係「本宮」過濾分開；不影響排除）。
   * 關係選「本宮」時一律以日干落宮為過濾／高亮目標。
   * 預設 true（僅影響非「本宮」關係時的附加高亮）。
   */
  benGong?: boolean;
  /** 最多掃描時辰數（預設 400 ≈ 一個月） */
  maxSlots?: number;
}

export interface PalaceHit {
  luoshu: number;
  name: string;
  layers: CuiyunLayer[];
  /** 該宮命中的層標籤（繁中） */
  layerLabels: string[];
  /** 命中細項顯示，如「門:生」「天干:戊」 */
  itemLabels: string[];
}

export interface CuiyunBoard {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  pillars: QimenResult['pillars'];
  dayStem: string;
  dayWx: Wuxing;
  /** 日干落宮洛書五行（生剋「我」） */
  meWx: Wuxing;
  /** 日干落宮洛書號（找不到則 null） */
  dayStemPalace: number | null;
  dayStemPalaceName: string;
  shengWo: PalaceHit[];
  woKe: PalaceHit[];
  /** 日干本宮層命中（關係＝本宮時用於過濾；其它模式亦填供顯示） */
  benGongHit: PalaceHit | null;
  /** 跨層生剋命中（未啟用或未命中則 null） */
  crossLayerHit: CrossLayerHit | null;
  menFuyin: string;
  ganFuyin: string;
  result: QimenResult;
}

export interface CuiyunSearchResult {
  boards: CuiyunBoard[];
  scanned: number;
  excludedFuyin: number;
  /** 日干落宮有刑／迫／墓／空而硬性排除 */
  excludedDayPalace: number;
  /** 生我宮有空／刑／墓／迫而排除 */
  excludedShengWoBad: number;
  /** 生我宮或日干本宮有庚丙／丙庚而排除 */
  excludedGengBing: number;
  truncated: boolean;
  layers: CuiyunLayer[];
  layerItems: LayerItemFilters;
  relation: RelationFilter;
  /** 啟用的跨層生剋（正規化後；未啟用則 null） */
  crossLayer: CrossLayerFilter | null;
  /** 附加高亮旗標（與排除無關；關係「本宮」另見 relation） */
  benGong: boolean;
}

/** 每日時辰取樣點（含早子 0、夜子 23） */
const SHICHEN_HOURS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 23] as const;

function toMs(y: number, m: number, d: number, h: number, mi: number): number {
  return new Date(y, m - 1, d, h, mi, 0, 0).getTime();
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function formatBoardTime(b: Pick<CuiyunBoard, 'year' | 'month' | 'day' | 'hour' | 'minute'>): string {
  return `${b.year}-${pad(b.month)}-${pad(b.day)} ${pad(b.hour)}:${pad(b.minute)}`;
}

/**
 * 日干落宮：天盤干或寄顯＝日干（甲→旬首六儀干）
 * 中五無天盤飛布時以寄坤（宮2）寄顯為準；優先非中宮。
 */
export function findDayStemPalace(result: QimenResult): number | null {
  const dayStem = result.pillars.day.charAt(0);
  const target = dayStem === '甲' ? result.xunshouStem : dayStem;
  if (!target) return null;

  let midHit: number | null = null;
  for (const p of result.palaces) {
    if (p.tianGan === target || p.jiXianTian === target) {
      if (p.luoshu === 5) {
        midHit = 5;
        continue;
      }
      return p.luoshu;
    }
  }
  return midHit;
}

function isFuyinOrFanyin(result: QimenResult): boolean {
  return Boolean(result.menFuyin || result.ganFuyin);
}

/** 宮有空亡／擊刑／入墓／門迫 */
function palaceHasBadMarkers(p: PalaceContent): boolean {
  return Boolean(p.kongWang || p.xing || p.mu || p.po);
}

/**
 * 宮內天盤干（含寄顯）× 地盤干（含寄顯）是否為 庚+丙 或 丙+庚。
 * 與盤面天干組合相同：天側 [tianGan, jiXianTian] × 地側 [diGan, jiXianDi]。
 */

/** 可催運生剋宮：排除中宮（洛書5）。中五內容寄坤僅影響盤面寄顯，不列入生我／我剋。 */
export function isCuiyunRelationPalace(luoshu: number): boolean {
  return luoshu >= 1 && luoshu <= 9 && luoshu !== 5;
}

export function palaceHasGengBing(p: PalaceContent): boolean {
  const tianStems = [p.tianGan, p.jiXianTian].filter(Boolean);
  const diStems = [p.diGan, p.jiXianDi].filter(Boolean);
  for (const t of tianStems) {
    for (const d of diStems) {
      if ((t === '庚' && d === '丙') || (t === '丙' && d === '庚')) return true;
    }
  }
  return false;
}

/** 日干本宮或任一洛書生我宮有庚丙／丙庚 */
function keyPalacesHaveGengBing(
  result: QimenResult,
  dayStemPalace: number | null,
  meWx: Wuxing | null,
): boolean {
  if (dayStemPalace != null) {
    const dp = result.palaces.find((x) => x.luoshu === dayStemPalace);
    if (dp && palaceHasGengBing(dp)) return true;
  }
  if (meWx) {
    for (const p of result.palaces) {
      if (!isCuiyunRelationPalace(p.luoshu)) continue;
      const pWx = palaceWuxing(p.luoshu);
      if (pWx && isShengWo(pWx, meWx) && palaceHasGengBing(p)) return true;
    }
  }
  return false;
}

function dayPalaceBad(result: QimenResult, palaceNo: number | null): boolean {
  if (palaceNo == null) return true; // 找不到日干落宮 → 不可催（保守排除）
  const p = result.palaces.find((x) => x.luoshu === palaceNo);
  if (!p) return true;
  return palaceHasBadMarkers(p);
}

function normalizeFilters(raw?: LayerItemFilters): LayerItemFilters {
  const out: LayerItemFilters = {};
  if (!raw) return out;
  for (const layer of ['gan', 'men', 'xing', 'shen'] as CuiyunLayer[]) {
    const arr = raw[layer];
    if (!arr || !arr.length) continue;
    const ids = [...new Set(arr.map(normalizeLayerItem).filter(Boolean))];
    if (ids.length) out[layer] = ids;
  }
  return out;
}

/**
 * 依洛書宮位五行收集生我／我剋宮。
 * meWx = 日干落宮之洛書五行；層＋可選細項過濾。
 */
function collectHits(
  result: QimenResult,
  meWx: Wuxing,
  layers: CuiyunLayer[],
  layerItems: LayerItemFilters,
  kind: '生我' | '我剋',
): PalaceHit[] {
  const out: PalaceHit[] = [];
  for (const p of result.palaces) {
    if (!isCuiyunRelationPalace(p.luoshu)) continue; // 排除中宮
    const pWx = palaceWuxing(p.luoshu);
    if (!pWx) continue;
    const ok = kind === '生我' ? isShengWo(pWx, meWx) : isWoKe(pWx, meWx);
    if (!ok) continue;

    const star = p.tianPanXing || p.star;
    const hitLayers: CuiyunLayer[] = [];
    const itemLabels: string[] = [];
    for (const layer of layers) {
      const selected = layerItems[layer];
      if (layerMatches(layer, p.tianGan, p.door, star, p.shen, selected)) {
        hitLayers.push(layer);
        const id = layerItemId(layer, p.tianGan, p.door, star, p.shen);
        const lab = layerItemLabel(layer, id);
        if (lab) {
          // 有細項篩選時標出具體值；否則仍標層名＋值方便辨識
          itemLabels.push(`${LAYER_LABEL[layer]}:${lab}`);
        }
      }
    }
    // 無啟用層或該宮無任何選定層內容 → 略過
    if (!hitLayers.length) continue;

    out.push({
      luoshu: p.luoshu,
      name: p.name,
      layers: hitLayers,
      layerLabels: hitLayers.map((l) => LAYER_LABEL[l]),
      itemLabels,
    });
  }
  return out;
}

/**
 * 正規化跨層過濾：缺任一端／關係 → null；id 正規化；samePalace 預設 false。
 */
export function normalizeCrossLayer(
  raw?: CrossLayerFilter | null,
): CrossLayerFilter | null {
  if (!raw || !raw.a || !raw.b || (raw.relation !== '生' && raw.relation !== '剋')) {
    return null;
  }
  const aId = normalizeLayerItem(raw.a.id);
  const bId = normalizeLayerItem(raw.b.id);
  if (!aId || !bId) return null;
  const layers: CuiyunLayer[] = ['gan', 'men', 'xing', 'shen'];
  if (!layers.includes(raw.a.layer) || !layers.includes(raw.b.layer)) return null;
  return {
    a: { layer: raw.a.layer, id: aId },
    relation: raw.relation,
    b: { layer: raw.b.layer, id: bId },
    samePalace: Boolean(raw.samePalace),
  };
}

/** 八宮（不含中5）中，該層符號出現的洛書號 */
export function findSymbolPalaces(
  result: QimenResult,
  layer: CuiyunLayer,
  rawId: string,
): number[] {
  const want = normalizeLayerItem(rawId);
  if (!want) return [];
  const out: number[] = [];
  for (const p of result.palaces) {
    if (!isCuiyunRelationPalace(p.luoshu)) continue;
    const star = p.tianPanXing || p.star;
    const id = layerItemId(layer, p.tianGan, p.door, star, p.shen);
    if (id === want) out.push(p.luoshu);
  }
  return out;
}

/**
 * 跨層生剋匹配（洛書宮位五行）。
 *
 * 放置規則（見 wuxing.ts 頭註）：
 *   - 在八宮（排除中5）找含 A、含 B 的宮；星層中五固定禽→芮不參與
 *   - 存在 (Pa,Pb)：Pa 洛書五行 生/剋 Pb 洛書五行（與日干落宮生我／我剋同表）
 *   - **不用** 符號固有五行（乙≠木剋芮土；合≠木生乙）
 *   - samePalace=false（預設）：Pa、Pb 可異宮
 *   - samePalace=true：須 Pa===Pb（同宮五行相同→生/剋通常不成立）
 * 例：乙剋芮＝乙宮洛書剋飛布芮宮洛書；合生乙＝合宮洛書生乙宮洛書
 */
export function matchCrossLayerShengKe(
  result: QimenResult,
  filter: CrossLayerFilter,
): CrossLayerHit | null {
  const norm = normalizeCrossLayer(filter);
  if (!norm) return null;

  const aPalaces = findSymbolPalaces(result, norm.a.layer, norm.a.id);
  const bPalaces = findSymbolPalaces(result, norm.b.layer, norm.b.id);
  if (!aPalaces.length || !bPalaces.length) return null;

  const samePalace = Boolean(norm.samePalace);
  const pairs: CrossLayerPair[] = [];
  for (const pa of aPalaces) {
    for (const pb of bPalaces) {
      if (samePalace && pa !== pb) continue;
      if (!palacesElementRelate(pa, norm.relation, pb)) continue;
      const aWx = palaceWuxing(pa)!;
      const bWx = palaceWuxing(pb)!;
      pairs.push({ aPalace: pa, bPalace: pb, aWx, bWx });
    }
  }
  if (!pairs.length) return null;

  const highlightPalaces = [
    ...new Set(pairs.flatMap((x) => [x.aPalace, x.bPalace])),
  ].sort((a, b) => a - b);

  const aLab = layerItemLabel(norm.a.layer, norm.a.id);
  const bLab = layerItemLabel(norm.b.layer, norm.b.id);
  const first = pairs[0];
  const nameOf = (n: number) =>
    result.palaces.find((x) => x.luoshu === n)?.name ?? String(n);
  const label =
    pairs.length === 1
      ? `${aLab}@${nameOf(first.aPalace)}${first.aPalace}(${first.aWx})${norm.relation}${bLab}@${nameOf(first.bPalace)}${first.bPalace}(${first.bWx})`
      : `${aLab}${norm.relation}${bLab}×${pairs.length}`;

  return {
    aPalaces,
    bPalaces,
    pairs,
    highlightPalaces,
    label,
    samePalace,
  };
}

/**
 * 日干本宮是否命中啟用層＋細項。
 * 例：layers=['men']、layerItems={men:['生']} → 本宮門須為生。
 */
export function matchBenGongPalace(
  result: QimenResult,
  dayStemPalace: number | null,
  layers: CuiyunLayer[],
  layerItems: LayerItemFilters,
): PalaceHit | null {
  if (dayStemPalace == null) return null;
  const p = result.palaces.find((x) => x.luoshu === dayStemPalace);
  if (!p) return null;
  const star = p.tianPanXing || p.star;
  const hitLayers: CuiyunLayer[] = [];
  const itemLabels: string[] = [];
  for (const layer of layers) {
    const selected = layerItems[layer];
    if (layerMatches(layer, p.tianGan, p.door, star, p.shen, selected)) {
      hitLayers.push(layer);
      const id = layerItemId(layer, p.tianGan, p.door, star, p.shen);
      const lab = layerItemLabel(layer, id);
      if (lab) itemLabels.push(`${LAYER_LABEL[layer]}:${lab}`);
    }
  }
  if (!hitLayers.length) return null;
  return {
    luoshu: p.luoshu,
    name: p.name,
    layers: hitLayers,
    layerLabels: hitLayers.map((l) => LAYER_LABEL[l]),
    itemLabels,
  };
}

function analyzeBoard(
  result: QimenResult,
  layers: CuiyunLayer[],
  layerItems: LayerItemFilters,
  _benGong: boolean = true,
  crossLayer: CrossLayerFilter | null = null,
): CuiyunBoard | null {
  const dayStem = result.pillars.day.charAt(0);
  const dayWx = stemWuxing(dayStem);
  if (!dayWx) return null;

  if (isFuyinOrFanyin(result)) return null;

  const dayStemPalace = findDayStemPalace(result);
  // 硬性排除：日干本宮有空／刑／墓／迫（與 benGong 高亮開關無關）
  if (dayPalaceBad(result, dayStemPalace)) return null;

  const meWx = dayStemPalace != null ? palaceWuxing(dayStemPalace) : null;
  if (!meWx) return null;

  // 硬性排除：生我宮或日干本宮有庚丙／丙庚
  if (keyPalacesHaveGengBing(result, dayStemPalace, meWx)) return null;

  // 硬性排除：任一洛書生我宮有凶標（與細項無關；不含中宮）
  for (const p of result.palaces) {
    if (!isCuiyunRelationPalace(p.luoshu)) continue;
    const pWx = palaceWuxing(p.luoshu);
    if (pWx && isShengWo(pWx, meWx) && palaceHasBadMarkers(p)) return null;
  }

  const shengWo = collectHits(result, meWx, layers, layerItems, '生我');
  const woKe = collectHits(result, meWx, layers, layerItems, '我剋');
  const benGongHit = matchBenGongPalace(result, dayStemPalace, layers, layerItems);
  const crossLayerHit = crossLayer
    ? matchCrossLayerShengKe(result, crossLayer)
    : null;

  const inp = result.input;
  const palaceName =
    dayStemPalace != null
      ? (result.palaces.find((p) => p.luoshu === dayStemPalace)?.name ?? '')
      : '';

  return {
    year: inp.year,
    month: inp.month,
    day: inp.day,
    hour: inp.hour,
    minute: inp.minute,
    pillars: result.pillars,
    dayStem,
    dayWx,
    meWx,
    dayStemPalace,
    dayStemPalaceName: palaceName,
    shengWo,
    woKe,
    benGongHit,
    crossLayerHit,
    menFuyin: result.menFuyin,
    ganFuyin: result.ganFuyin,
    result,
  };
}

/** 掃描區間內時辰，回傳通過排除＆（可選）生剋／本宮／細項過濾的盤 */
export function searchCuiyun(input: CuiyunSearchInput): CuiyunSearchResult {
  const layers: CuiyunLayer[] =
    input.layers && input.layers.length
      ? [...input.layers]
      : (['gan', 'men', 'xing', 'shen'] as CuiyunLayer[]);
  const layerItems = normalizeFilters(input.layerItems);
  const relation = input.relation ?? null;
  const crossLayer = normalizeCrossLayer(input.crossLayer ?? null);
  const benGong = input.benGong !== false; // 預設開（僅高亮；排除不依此）
  const maxSlots = input.maxSlots ?? 400;

  const fromMs = toMs(
    input.from.year,
    input.from.month,
    input.from.day,
    input.from.hour,
    input.from.minute,
  );
  const toMsVal = toMs(
    input.to.year,
    input.to.month,
    input.to.day,
    input.to.hour,
    input.to.minute,
  );

  const boards: CuiyunBoard[] = [];
  let scanned = 0;
  let excludedFuyin = 0;
  let excludedDayPalace = 0;
  let excludedShengWoBad = 0;
  let excludedGengBing = 0;
  let truncated = false;

  if (toMsVal < fromMs) {
    return {
      boards,
      scanned,
      excludedFuyin,
      excludedDayPalace,
      excludedShengWoBad,
      excludedGengBing,
      truncated,
      layers,
      layerItems,
      relation,
      crossLayer,
      benGong,
    };
  }

  // 由 from 日期走至 to 日期
  const cursor = new Date(input.from.year, input.from.month - 1, input.from.day, 0, 0, 0, 0);
  const endDay = new Date(input.to.year, input.to.month - 1, input.to.day, 0, 0, 0, 0);

  while (cursor.getTime() <= endDay.getTime()) {
    const y = cursor.getFullYear();
    const m = cursor.getMonth() + 1;
    const d = cursor.getDate();

    for (const h of SHICHEN_HOURS) {
      const mi = 30;
      const t = toMs(y, m, d, h, mi);
      if (t < fromMs || t > toMsVal) continue;

      scanned += 1;
      if (scanned > maxSlots) {
        truncated = true;
        break;
      }

      const result = computeQimen({ year: y, month: m, day: d, hour: h, minute: mi });

      if (isFuyinOrFanyin(result)) {
        excludedFuyin += 1;
        continue;
      }
      const dsp = findDayStemPalace(result);
      const meWx = dsp != null ? palaceWuxing(dsp) : null;

      // 硬性：生我宮或日干本宮有庚丙／丙庚
      if (keyPalacesHaveGengBing(result, dsp, meWx)) {
        excludedGengBing += 1;
        continue;
      }

      // 硬性：日干本宮有空／刑／墓／迫
      if (dayPalaceBad(result, dsp)) {
        excludedDayPalace += 1;
        continue;
      }

      // 生我宮凶標：凡洛書生我宮有空／刑／墓／迫即排除（不看層／細項；不含中宮）
      if (meWx) {
        let swBad = false;
        for (const p of result.palaces) {
          if (!isCuiyunRelationPalace(p.luoshu)) continue;
          const pWx = palaceWuxing(p.luoshu);
          if (pWx && isShengWo(pWx, meWx) && palaceHasBadMarkers(p)) {
            swBad = true;
            break;
          }
        }
        if (swBad) {
          excludedShengWoBad += 1;
          continue;
        }
      }

      const board = analyzeBoard(result, layers, layerItems, benGong, crossLayer);
      if (!board) continue;

      if (relation === '生我' && board.shengWo.length === 0) continue;
      if (relation === '我剋' && board.woKe.length === 0) continue;
      if (relation === '本宮' && !board.benGongHit) continue;
      // 跨層生剋：啟用時須命中（與日干落宮模式 AND）
      if (crossLayer && !board.crossLayerHit) continue;

      boards.push(board);
    }
    if (truncated) break;
    cursor.setDate(cursor.getDate() + 1);
  }

  return {
    boards,
    scanned,
    excludedFuyin,
    excludedDayPalace,
    excludedShengWoBad,
    excludedGengBing,
    truncated,
    layers,
    layerItems,
    relation,
    crossLayer,
    benGong,
  };
}

/** 宮是否在命中列表 */
export function palaceInHits(hits: PalaceHit[], luoshu: number): boolean {
  return hits.some((h) => h.luoshu === luoshu);
}

/** 單盤摘要文字 */
export function summarizeHits(hits: PalaceHit[]): string {
  if (!hits.length) return '—';
  return hits
    .map((h) => {
      const detail =
        h.itemLabels && h.itemLabels.length
          ? h.itemLabels.join('、')
          : h.layerLabels.join('、');
      return `${h.name}${h.luoshu}(${detail})`;
    })
    .join('、');
}

/** 匯出供測試：對已算好的 result 做排除＋生剋（不掃描） */
export function evaluateCuiyunBoard(
  result: QimenResult,
  layers: CuiyunLayer[] = ['gan', 'men', 'xing', 'shen'],
  layerItems: LayerItemFilters = {},
  benGong: boolean = true,
  crossLayer: CrossLayerFilter | null = null,
): { ok: boolean; reason?: string; board?: CuiyunBoard } {
  if (isFuyinOrFanyin(result)) {
    return {
      ok: false,
      reason: [result.menFuyin, result.ganFuyin].filter(Boolean).join('·') || '伏反吟',
    };
  }
  const dsp = findDayStemPalace(result);
  const meWxCheck = dsp != null ? palaceWuxing(dsp) : null;
  if (keyPalacesHaveGengBing(result, dsp, meWxCheck)) {
    return { ok: false, reason: '生我宮或日干本宮有庚丙／丙庚' };
  }
  if (dayPalaceBad(result, dsp)) {
    const p = dsp != null ? result.palaces.find((x) => x.luoshu === dsp) : null;
    const bits = [
      p?.kongWang ? '空亡' : '',
      p?.xing ? '擊刑' : '',
      p?.mu ? '入墓' : '',
      p?.po ? '迫' : '',
      dsp == null ? '無日干落宮' : '',
    ].filter(Boolean);
    return { ok: false, reason: `日干落宮${dsp ?? '?'}：${bits.join('／')}` };
  }
  if (meWxCheck) {
    for (const p of result.palaces) {
      if (!isCuiyunRelationPalace(p.luoshu)) continue;
      const pWx = palaceWuxing(p.luoshu);
      if (pWx && isShengWo(pWx, meWxCheck) && palaceHasBadMarkers(p)) {
        return { ok: false, reason: '生我宮有空／刑／墓／迫' };
      }
    }
  }
  const cl = normalizeCrossLayer(crossLayer);
  const board = analyzeBoard(result, layers, normalizeFilters(layerItems), benGong, cl);
  if (!board) {
    return { ok: false, reason: '無法分析' };
  }
  if (cl && !board.crossLayerHit) {
    return { ok: false, reason: '跨層生剋未命中' };
  }
  return { ok: true, board };
}

/**
 * 測試輔助：宮是否因洛書生剋命中，且該層有內容（可加細項）。
 * meWx = 日干落宮洛書五行。
 */
export function palaceLayerMatch(
  p: PalaceContent,
  meWx: Wuxing,
  layer: CuiyunLayer,
  kind: '生我' | '我剋',
  selected?: string[] | null,
): boolean {
  if (!isCuiyunRelationPalace(p.luoshu)) return false;
  const pWx = palaceWuxing(p.luoshu);
  if (!pWx) return false;
  const ok = kind === '生我' ? isShengWo(pWx, meWx) : isWoKe(pWx, meWx);
  if (!ok) return false;
  const star = p.tianPanXing || p.star;
  return layerMatches(layer, p.tianGan, p.door, star, p.shen, selected);
}

/** 測試／匯出：純洛書生剋宮號（不看層） */
export function luoshuRelationPalaces(
  meWx: Wuxing,
  kind: '生我' | '我剋',
): number[] {
  const out: number[] = [];
  for (let n = 1; n <= 9; n++) {
    if (!isCuiyunRelationPalace(n)) continue; // 不含中宮
    const pWx = palaceWuxing(n);
    if (!pWx) continue;
    const ok = kind === '生我' ? isShengWo(pWx, meWx) : isWoKe(pWx, meWx);
    if (ok) out.push(n);
  }
  return out;
}
